/**
 * Defines internal response workflow, public tracking and response outbox events.
 */
import { z } from "zod";
import { PublicResponseChannelSchema } from "./PublicCaseContract.js";
export const ResponseDraftStatusSchema = z.enum([
    "WORKING",
    "PENDING_APPROVAL",
    "APPROVED",
    "REJECTED",
]);
export const ResponseDeliveryStatusSchema = z.enum([
    "PENDING",
    "ACCEPTED",
    "DELIVERED",
    "FAILED",
    "AWAITING_DISPATCH",
    "DISPATCHED",
]);
const expectedVersionSchema = z.number().int().nonnegative();
const reasonSchema = z.string().trim().min(5).max(1_000);
const optionalResponseTextSchema = z.string().trim().max(12_000);
export const SaveResponseDraftRequestSchema = z
    .object({
    actionsTaken: optionalResponseTextSchema,
    baseDraftId: z.uuid().nullable(),
    complaintResponse: optionalResponseTextSchema,
    correctionOfFinalResponseId: z.uuid().nullable(),
    correctionReason: z.string().trim().min(5).max(2_000).nullable(),
    evaluationSummary: optionalResponseTextSchema,
    expectedVersion: expectedVersionSchema,
    internalResult: optionalResponseTextSchema,
    requestedActionResponse: optionalResponseTextSchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.correctionOfFinalResponseId === null) !== (value.correctionReason === null)) {
        context.addIssue({
            code: "custom",
            message: "A correction requires both the prior response and a reason.",
            path: ["correctionReason"],
        });
    }
    const hasContent = [
        value.actionsTaken,
        value.complaintResponse,
        value.evaluationSummary,
        value.internalResult,
        value.requestedActionResponse,
    ].some((field) => field.length > 0);
    if (!hasContent) {
        context.addIssue({
            code: "custom",
            message: "At least one response field must contain text.",
            path: ["evaluationSummary"],
        });
    }
});
/**
 * Formal consumer-facing fields required before submit/approve.
 */
export const FormalResponseContentFields = Object.freeze([
    "evaluationSummary",
    "complaintResponse",
    "requestedActionResponse",
    "actionsTaken",
    "internalResult",
]);
export const SubmitResponseDraftRequestSchema = z
    .object({
    draftId: z.uuid(),
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
})
    .strict();
/**
 * Withdraws a pending draft back to working preparation so it can be edited again.
 */
export const WithdrawResponseDraftRequestSchema = z
    .object({
    draftId: z.uuid(),
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
})
    .strict();
export const DecideResponseDraftRequestSchema = z
    .object({
    decision: z.enum(["APPROVE", "REJECT"]),
    draftId: z.uuid(),
    expectedVersion: expectedVersionSchema,
    observations: z.string().trim().max(2_000).nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.decision === "REJECT" &&
        (value.observations === null || value.observations.length < 5)) {
        context.addIssue({
            code: "custom",
            message: "Rejection observations are required.",
            path: ["observations"],
        });
    }
    if (value.decision === "APPROVE" && value.observations !== null) {
        context.addIssue({
            code: "custom",
            message: "Approval does not accept mutable observations.",
            path: ["observations"],
        });
    }
});
export const DispatchLetterResponseRequestSchema = z
    .object({
    deliveryId: z.uuid(),
    dispatchedAt: z.iso.datetime({ offset: true }),
    expectedVersion: expectedVersionSchema,
    observations: z.string().trim().max(2_000).nullable(),
    operator: z.string().trim().min(2).max(200),
    trackingNumber: z.string().trim().min(2).max(200),
})
    .strict();
/**
 * Admin confirmation that an email response was communicated when automated delivery is pending/failed.
 */
export const ConfirmEmailResponseRequestSchema = z
    .object({
    communicatedAt: z.iso.datetime({ offset: true }),
    deliveryId: z.uuid(),
    expectedVersion: expectedVersionSchema,
    observations: z.string().trim().max(2_000).nullable(),
})
    .strict();
const actorReferenceSchema = z
    .object({
    displayName: z.string(),
})
    .strict();
const responseDraftSchema = z
    .object({
    actionsTaken: z.string(),
    author: actorReferenceSchema,
    complaintResponse: z.string(),
    correctionReason: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    decidedAt: z.iso.datetime({ offset: true }).nullable(),
    decidedBy: actorReferenceSchema.nullable(),
    decisionReason: z.string().nullable(),
    evaluationSummary: z.string(),
    id: z.uuid(),
    internalResult: z.string(),
    requestedActionResponse: z.string(),
    status: ResponseDraftStatusSchema,
    submittedAt: z.iso.datetime({ offset: true }).nullable(),
    submittedBy: actorReferenceSchema.nullable(),
    correctsFinalResponseId: z.uuid().nullable(),
    versionNumber: z.number().int().positive(),
})
    .strict();
const finalResponseSchema = z
    .object({
    actionsTaken: z.string(),
    approvedAt: z.iso.datetime({ offset: true }),
    approvedBy: actorReferenceSchema,
    communicatedAt: z.iso.datetime({ offset: true }).nullable(),
    complaintResponse: z.string(),
    correctionReason: z.string().nullable(),
    delivery: z
        .object({
        acceptedAt: z.iso.datetime({ offset: true }).nullable(),
        deliveredAt: z.iso.datetime({ offset: true }).nullable(),
        dispatchedAt: z.iso.datetime({ offset: true }).nullable(),
        id: z.uuid(),
        status: ResponseDeliveryStatusSchema,
        trackingNumber: z.string().nullable(),
    })
        .strict(),
    evaluationSummary: z.string(),
    id: z.uuid(),
    isCorrection: z.boolean(),
    requestedActionResponse: z.string(),
    responseChannel: PublicResponseChannelSchema,
    sequenceNumber: z.number().int().positive(),
})
    .strict();
export const CaseResponseWorkflowSchema = z
    .object({
    drafts: z.array(responseDraftSchema),
    finalResponses: z.array(finalResponseSchema),
    permissions: z
        .object({
        canConfirmEmail: z.boolean(),
        canDecide: z.boolean(),
        canDispatchLetter: z.boolean(),
        canDraft: z.boolean(),
        canSubmit: z.boolean(),
        canWithdraw: z.boolean(),
    })
        .strict(),
    publicId: z.string().regex(/^\d+$/),
    version: z.number().int().nonnegative(),
})
    .strict();
export const ResponseMutationResultSchema = z
    .object({
    draftId: z.uuid().nullable(),
    finalResponseId: z.uuid().nullable(),
    publicId: z.string().regex(/^\d+$/),
    version: z.number().int().nonnegative(),
    wasReplay: z.boolean(),
})
    .strict();
export const PublicCaseTrackingParametersSchema = z
    .object({
    publicId: z.string().regex(/^\d+$/),
})
    .strict();
export const PublicCaseTrackingHeadersSchema = z
    .object({
    authorization: z.string().regex(/^Bearer lrc_[A-Za-z0-9_-]{16}\.[A-Za-z0-9_-]{43}$/),
})
    .strict();
export const PublicCaseTrackingResponseSchema = z
    .object({
    bookNumber: z.string().nullable(),
    finalResponses: z.array(z
        .object({
        actionsTaken: z.string(),
        communicatedAt: z.iso.datetime({ offset: true }),
        complaintResponse: z.string(),
        correctionReason: z.string().nullable(),
        evaluationSummary: z.string(),
        isCorrection: z.boolean(),
        requestedActionResponse: z.string(),
        responseChannel: PublicResponseChannelSchema,
        sequenceNumber: z.number().int().positive(),
    })
        .strict()),
    providerName: z.string(),
    publicId: z.string().regex(/^\d+$/),
    publicStatus: z.enum([
        "RECEIVED",
        "IN_PROGRESS",
        "RESPONSE_PREPARATION",
        "RESPONDED",
        "CLOSED",
    ]),
    receivedAt: z.iso.datetime({ offset: true }),
})
    .strict();
const responseEventBaseSchema = z
    .object({
    branchId: z.uuid().nullable(),
    caseId: z.uuid(),
    companyId: z.uuid(),
    occurredAt: z.iso.datetime({ offset: true }),
    publicId: z.string().regex(/^\d+$/),
})
    .strict();
export const ResponseDraftSubmittedEventName = "responses.draft-submitted.v1";
export const ResponseDraftSubmittedEventPayloadSchema = responseEventBaseSchema
    .extend({
    authorActorId: z.uuid(),
    draftId: z.uuid(),
})
    .strict();
export const ResponseDraftRejectedEventName = "responses.draft-rejected.v1";
export const ResponseDraftRejectedEventPayloadSchema = responseEventBaseSchema
    .extend({
    authorActorId: z.uuid(),
    draftId: z.uuid(),
})
    .strict();
export const ResponseApprovedEventName = "responses.approved.v1";
export const ResponseApprovedEventPayloadSchema = responseEventBaseSchema
    .extend({
    authorActorId: z.uuid(),
    deliveryId: z.uuid(),
    finalResponseId: z.uuid(),
    responseChannel: PublicResponseChannelSchema,
})
    .strict();
export const ResponseSentEventName = "responses.sent.v1";
export const ResponseSentEventPayloadSchema = responseEventBaseSchema
    .extend({
    authorActorId: z.uuid(),
    finalResponseId: z.uuid(),
    responseChannel: PublicResponseChannelSchema,
})
    .strict();
//# sourceMappingURL=ResponseContract.js.map